import { ShopigurusHTMLElement } from '@shopigurus/base';

/**
 * ShpfltCartToast — Cart confirmation toast + badge counter updater.
 *
 * Replaces the previous auto-opening cart drawer with a less intrusive
 * top-center toast. Also keeps the cart badge counter
 * (.collection-sticky-cart-count) in sync as items are added/removed.
 *
 * @example
 * <shpflt-cart-toast data-shpflt>
 *   <div ref="toast" role="status" aria-live="polite">…</div>
 * </shpflt-cart-toast>
 *
 * @listens {liquid-ajax-cart:request-end}
 */
class ShpfltCartToast extends ShopigurusHTMLElement {
  /** @type {number|undefined} */
  #hideTimer;

  /** @type {number} ms */
  #visibleFor = 3000;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      'liquid-ajax-cart:request-end',
      (e) => this.#handleCartEnd(e),
      { signal: this.abortController.signal }
    );

    // Initial badge sync from the in-DOM cart count (server-rendered)
    queueMicrotask(() => this.#refreshBadge());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#hideTimer) {
      clearTimeout(this.#hideTimer);
      this.#hideTimer = undefined;
    }
  }

  /** @param {CustomEvent} e */
  #handleCartEnd(e) {
    const requestState = e.detail?.requestState;
    if (!requestState) return;

    // Always refresh the badge counter on any successful cart mutation
    if (requestState.responseData?.ok) {
      this.#refreshBadge();
    }

    // Show the toast specifically on a successful add
    if (requestState.requestType === 'add' && requestState.responseData?.ok) {
      this.#showToast();
    }
  }

  #showToast() {
    /** @type {HTMLElement} */
    const toast = this.refs.toast;
    if (!toast) return;

    toast.hidden = false;
    // Force reflow so the transition runs on the freshly-shown element
    void toast.offsetWidth;
    toast.dataset.visible = 'true';

    if (this.#hideTimer) clearTimeout(this.#hideTimer);
    this.#hideTimer = window.setTimeout(() => {
      toast.dataset.visible = 'false';
      // After the fade-out, hide from a11y tree completely
      const hideAfter = window.setTimeout(() => {
        if (toast.dataset.visible !== 'true') toast.hidden = true;
      }, 300);
      this.#hideTimer = hideAfter;
    }, this.#visibleFor);
  }

  /**
   * Read the live cart count from the data-ajax-cart-bind span Liquid Ajax Cart updates,
   * and propagate it to data-count on every badge instance for CSS hiding at 0.
   */
  #refreshBadge() {
    const sources = document.querySelectorAll('[data-ajax-cart-bind="item_count"]');
    if (!sources.length) return;

    let count = 0;
    for (const el of sources) {
      const parsed = parseInt((el.textContent || '0').trim(), 10);
      if (!Number.isNaN(parsed)) {
        count = parsed;
        break;
      }
    }

    for (const el of sources) {
      el.setAttribute('data-count', String(count));
      el.setAttribute('aria-label', `${count} producto(s) en la cesta`);
    }
  }
}

customElements.define('shpflt-cart-toast', ShpfltCartToast);
